import { useReducer } from 'react';

import {
  getFirstTestForGroup_templated,
  getFirstTestGroupForState_templated,
  getQuestionTypes_templated,
  getQuestionTypesForStateAndTestGroup,
  getTestHasUploadOptions_templated,
  QuestionTypeId,
  TestGroup,
  TestId,
} from '../components/dynamicPages/CreatePage/test-prep-tab/TestPrep.Utils';

export const LS_KEY_TEST_PREP_STATE = '@curipod_v1_createWithAI-TestPrepState';

export interface TestPrepState {
  selectedState: string; // e.g. "TX"
  selectedTestGroup?: string; // e.g. "STAAR"
  selectedTestGroupDropdown?: string;
  selectedTest?: string; // e.g. "STAAR RLA"
  selectedQuestionType?: string;
  isUpload?: boolean;
  lessonVariant?: string;
  selectedPage: number;
  hasUploadOptions?: boolean;
}

export type TestPrepAction =
  | { type: 'SET_SELECTED_STATE'; payload: string }
  | { type: 'SET_SELECTED_TEST_GROUP'; payload: string }
  | { type: 'SET_SELECTED_TEST_GROUP_DROPDOWN'; payload: string | undefined }
  | { type: 'SET_SELECTED_TEST'; payload: string }
  | { type: 'SET_SELECTED_QUESTION_TYPE'; payload: string | undefined }
  | { type: 'SET_IS_UPLOAD'; payload: boolean | undefined }
  | { type: 'SET_LESSON_VARIANT'; payload: string | undefined }
  | { type: 'SET_SELECTED_PAGE'; payload: number }
  | { type: 'GO_BACK'; payload: number }
  | { type: 'RESET' };

function templatedTestPrepReducer(
  state: TestPrepState,
  action: TestPrepAction,
): TestPrepState {
  switch (action.type) {
    case 'SET_SELECTED_STATE': {
      const selectedState = action.payload;
      const firstTestGroup = getFirstTestGroupForState_templated(selectedState);
      const firstTest = getFirstTestForGroup_templated(
        selectedState,
        firstTestGroup ?? '',
      );
      const firstQuestionType = firstTest
        ? getQuestionTypes_templated({
            testId: firstTest,
            generators: undefined,
            userAccessBadge: undefined,
          })[0].value
        : undefined;
      const hasUploadOptions = firstTest
        ? getTestHasUploadOptions_templated(firstTest, firstQuestionType)
        : undefined;
      return {
        ...state,
        selectedState,
        selectedTestGroup: firstTestGroup,
        selectedTest: firstTest,
        selectedQuestionType: firstQuestionType,
        hasUploadOptions,
      };
    }

    case 'SET_SELECTED_TEST_GROUP': {
      const firstTest = getFirstTestForGroup_templated(
        state.selectedState,
        action.payload,
      );
      const firstQuestionType = firstTest
        ? getQuestionTypes_templated({
            testId: firstTest,
            generators: undefined,
            userAccessBadge: undefined,
          })[0].value
        : undefined;
      const hasUploadOptions = firstTest
        ? getTestHasUploadOptions_templated(firstTest, firstQuestionType)
        : undefined;

      return {
        ...state,
        selectedTestGroup: action.payload,
        selectedTestGroupDropdown: action.payload,
        selectedTest: firstTest,
        selectedQuestionType: firstQuestionType,
        hasUploadOptions,
      };
    }

    case 'SET_SELECTED_TEST_GROUP_DROPDOWN':
      return { ...state, selectedTestGroupDropdown: action.payload };

    // this appears to be misnamed; it's used for subject
    case 'SET_SELECTED_TEST':
      const firstQuestionTypeForTest = getQuestionTypes_templated({
        testId: action.payload,
        generators: undefined,
        userAccessBadge: undefined,
      })[0].value;
      const hasUploadOptionsForTest = getTestHasUploadOptions_templated(
        action.payload,
        firstQuestionTypeForTest,
      );
      return {
        ...state,
        selectedTest: action.payload,
        selectedQuestionType: firstQuestionTypeForTest,
        hasUploadOptions: hasUploadOptionsForTest,
      };

    case 'SET_SELECTED_QUESTION_TYPE':
      const hasUploadOptionsForQuestionType = state.selectedTest
        ? getTestHasUploadOptions_templated(state.selectedTest, action.payload)
        : undefined;

      return {
        ...state,
        selectedQuestionType: action.payload,
        hasUploadOptions: hasUploadOptionsForQuestionType,
      };

    case 'SET_IS_UPLOAD':
      return { ...state, isUpload: action.payload };

    case 'SET_LESSON_VARIANT':
      return { ...state, lessonVariant: action.payload };

    case 'SET_SELECTED_PAGE':
      return { ...state, selectedPage: action.payload, isUpload: undefined };

    case 'GO_BACK':
      return {
        ...state,
        isUpload: undefined,
        lessonVariant: undefined,
        selectedPage: state.hasUploadOptions ? action.payload : 1,
      };

    case 'RESET':
      return initTestPrepStateTemplated({ presetState: state.selectedState });

    default:
      return state;
  }
}

function initTestPrepStateTemplated({
  // the difference between presetState and cardState is that presetState is overridden by whatever
  // we find in local storage, but cardState is not. maybe there's a better naming convention than 'card_', pls change if you think of it.
  presetState,
  cardState,
  cardTestGroup,
  cardTestId,
  cardQuestionType,
}: {
  presetState?: string;
  cardState?: string;
  cardTestGroup?: TestGroup;
  cardTestId?: TestId;
  cardQuestionType?: QuestionTypeId;
}): TestPrepState {
  const localTestPrepState =
    typeof window !== 'undefined' ? localStorage.getItem(LS_KEY_TEST_PREP_STATE) : null;

  const parsedUSState = localTestPrepState
    ? JSON.parse(localTestPrepState).selectedState
    : null;

  const selectedState = cardState
    ? cardState
    : parsedUSState
    ? parsedUSState
    : presetState
    ? presetState
    : 'TX';

  const parsedTestPrepGroup = localTestPrepState
    ? JSON.parse(localTestPrepState).selectedTestCategory
    : null;

  const selectedTestGroup = cardTestGroup
    ? cardTestGroup
    : parsedTestPrepGroup
    ? parsedTestPrepGroup
    : getFirstTestGroupForState_templated(selectedState); // e.g. "STAAR"

  const parsedTest = localTestPrepState
    ? JSON.parse(localTestPrepState).selectedTest
    : null;

  const isParsedTestMatchCardParams = getQuestionTypesForStateAndTestGroup(
    selectedState,
    selectedTestGroup,
  ).includes(parsedTest);

  const selectedTest = cardTestId
    ? cardTestId
    : isParsedTestMatchCardParams
    ? parsedTest
    : getFirstTestForGroup_templated(selectedState, selectedTestGroup);

  const selectedQuestionType = cardQuestionType
    ? cardQuestionType
    : selectedTest
    ? getQuestionTypes_templated({
        testId: selectedTest,
        generators: undefined,
        userAccessBadge: undefined,
      })?.[0]?.value
    : undefined;
  const hasUploadOptions = selectedQuestionType
    ? getTestHasUploadOptions_templated(selectedTest, selectedQuestionType)
    : undefined;

  return {
    selectedState,
    selectedTestGroup,
    selectedTestGroupDropdown: selectedTestGroup, // default to the same as selectedTestGroup
    selectedTest,
    selectedQuestionType,
    isUpload: undefined,
    lessonVariant: undefined,
    selectedPage: 1,
    hasUploadOptions,
  };
}

export function useTestPrepState({
  region,
  cardState,
  cardTestGroup,
  cardTestId,
  cardQuestionType,
}: {
  region?: string;
  cardState?: string;
  cardTestGroup?: TestGroup;
  cardTestId?: TestId;
  cardQuestionType?: QuestionTypeId;
}) {
  // Initialize the reducer
  const [testPrepState, dispatch] = useReducer(
    templatedTestPrepReducer,
    { presetState: region, cardState, cardTestGroup, cardTestId, cardQuestionType },
    initTestPrepStateTemplated,
  );

  // Return
  return { testPrepState, dispatch };
}
